import { ArrowDown, ArrowUp, ExternalLink } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { usd } from "@/components/dash/utils";

export const STATUS_STYLE = {
  DATA_UNAVAILABLE: { cls: "bg-[#7F1D1D] text-red-100", sub: "No estimate is produced while data is invalid" },
  INSUFFICIENT_EVIDENCE: { cls: "bg-[#1A2338] text-slate-200", sub: "Nothing material to report" },
  MIXED_INDICATORS: { cls: "bg-[#78350F] text-amber-100", sub: "Relevant indicators disagree — no directional conclusion" },
  MODEL_UNVALIDATED: { cls: "bg-[#1E293B] text-slate-100", sub: "Experimental model — not shown to beat the market on held-out history" },
  ESTIMATE_UPDATED: { cls: "bg-[#1E3A5F] text-blue-100", sub: "Valid estimate; not a directional conclusion" },
};
const utc = (t) => (t ? new Date(t * 1000).toISOString().slice(11, 19) + " UTC" : "n/a");

const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

const OutcomeBtn = ({ side, q }) => {
  const up = side === "Up";
  const Icon = up ? ArrowUp : ArrowDown;
  return (
    <div data-testid={`poly-btn-${side.toLowerCase()}`}
      className={`rounded-xl py-3 px-4 flex items-center justify-between font-head font-bold text-lg transition-transform ${up ? "bg-[#10B981]/15 text-[#34D399]" : "bg-[#EF4444]/15 text-[#F87171]"}`}>
      <span className="flex items-center gap-1"><Icon className="w-5 h-5" />{side}</span>
      <span className="font-num">{Math.round(q.ask * 100)}¢</span>
    </div>
  );
};

const BetCalc = ({ side, q, bet, feeRate }) => {
  const fee = feeRate * q.ask * (1 - q.ask);
  const shares = bet / (q.ask + fee);
  return (
    <p className="text-[11px] text-slate-400 mt-3 leading-relaxed" data-testid="bet-calculator">
      <b className="text-slate-200">Payout calculator (not advice):</b> {usd(bet)} on "{side}" at {Math.round(q.ask * 100)}¢ ≈ {shares.toFixed(1)} shares.
      If {side} wins you get <b className="text-[#34D399]">{usd(shares)}</b> (profit {usd(shares - bet)}). If it loses you lose {usd(bet)}.
    </p>
  );
};

const Evidence = ({ e }) => {
  if (!e) return null;
  return (
    <div className="mt-3 text-[11px] opacity-90 leading-relaxed" data-testid="evidence-panel">
      <div className="text-[10px] uppercase tracking-widest font-num opacity-80">Model evidence</div>
      <span data-testid="evidence-validated">{e.validated ? "Backtest-validated" : "Not validated as better than the market"}</span>
      {e.n ? ` · Brier model ${e.model_brier} vs market ${e.market_brier} over ${e.n} historical checks (lower is better)` : " · backtest pending"}
    </div>
  );
};

export const PolyHero = ({ live, lp, now, rules }) => {
  if (!live) return <section className="card p-5"><Skeleton className="h-72 bg-[#111A2C]" /></section>;
  // single source of truth: the backend result object (same one Telegram uses)
  const quotes = live.quotes;
  const upPct = Math.round((quotes.Up.display ?? (quotes.Up.bid + quotes.Up.ask) / 2) * 100);
  const start = live.btc.start;
  const price = live.btc.price;
  const diff = price - start;
  const remaining = Math.max(0, Math.round(live.market.end_ts - now / 1000));
  const v = STATUS_STYLE[live.status] || STATUS_STYLE.INSUFFICIENT_EVIDENCE;
  const leader = upPct >= 50 ? "Up" : "Down";
  const leaderPct = leader === "Up" ? upPct : 100 - upPct;

  return (
    <section className="card overflow-hidden fade-up" data-testid="poly-hero">
      <div className="p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <img src="https://polymarket-upload.s3.us-east-2.amazonaws.com/BTC+fullsize.png" alt="BTC" className="w-11 h-11 rounded-lg" />
          <div className="flex-1 min-w-0">
            <h2 className="font-head font-bold text-base sm:text-lg leading-tight" data-testid="poly-title">{live.market.question}</h2>
            <a href={live.market.url} target="_blank" rel="noreferrer" className="text-xs text-[#22D3EE] inline-flex items-center gap-1 mt-1" data-testid="poly-open-link">
              Open on Polymarket US <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mt-5">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-num text-slate-400">Price to beat</div>
            <div className="font-num font-semibold text-sm sm:text-base text-slate-300" data-testid="poly-price-to-beat">{usd(start)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-num text-slate-400">Current price</div>
            <div className={`font-num font-bold text-sm sm:text-base ${diff >= 0 ? "text-[#34D399]" : "text-[#F87171]"}`} data-testid="poly-current-price">{usd(price)}</div>
            <div className={`font-num text-[11px] ${diff >= 0 ? "text-[#34D399]" : "text-[#F87171]"}`}>{diff >= 0 ? "▲" : "▼"} {usd(Math.abs(diff))}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest font-num text-slate-400">Ends in</div>
            <div className="font-num font-bold text-xl sm:text-2xl text-[#F87171]" data-testid="poly-countdown">{mmss(remaining)}</div>
          </div>
        </div>

        <div className="mt-6 flex items-end justify-between">
          <div>
            <div className="font-num font-extrabold text-5xl sm:text-6xl tracking-tight" data-testid="poly-up-pct">{upPct}%</div>
            <div className="text-sm text-slate-400 mt-1">chance <b className="text-[#34D399]">Up</b></div>
          </div>
          <div className="text-right text-xs text-slate-400 max-w-[50%]">
            The crowd thinks <b className={leader === "Up" ? "text-[#34D399]" : "text-[#F87171]"}>{leader}</b> wins ({leaderPct}%).
          </div>
        </div>
        <div className="h-3 rounded-full overflow-hidden flex mt-3" data-testid="poly-odds-bar">
          <div className="bg-[#10B981] transition-[width] duration-700" style={{ width: `${upPct}%` }} />
          <div className="bg-[#EF4444] flex-1" />
        </div>
        <div className="flex justify-between text-[11px] font-num mt-1">
          <span className="text-[#34D399]">Up {upPct}%</span><span className="text-[#F87171]">Down {100 - upPct}%</span>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-5">
          <OutcomeBtn side="Up" q={quotes.Up} />
          <OutcomeBtn side="Down" q={quotes.Down} />
        </div>
        {rules && <BetCalc side={upPct < 50 ? "Down" : "Up"} q={upPct < 50 ? quotes.Down : quotes.Up} bet={rules.bet_size} feeRate={rules.fee_rate} />}
      </div>

      <div className={`px-5 sm:px-6 py-4 ${v.cls}`} data-testid="poly-verdict">
        <div className="text-[10px] uppercase tracking-widest font-num opacity-80">C · Analytics status · informational only</div>
        <div className="font-head font-extrabold text-2xl tracking-tight" data-testid="poly-verdict-label">{live.status_label}</div>
        <div className="text-sm opacity-90">{v.sub}</div>
        <p className="text-xs mt-2 opacity-90 leading-relaxed" data-testid="poly-verdict-reason">{live.explanation}</p>
        <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] font-num">
          <div data-testid="poly-market-implied"><div className="opacity-70">A · Observed market</div>Up {(live.market_up * 100).toFixed(1)}%</div>
          <div data-testid="poly-model-estimate"><div className="opacity-70">B · Model ({live.result?.model?.validation || "EXPERIMENTAL"})</div>
            Up {(live.model_up * 100).toFixed(1)}%{live.result?.model?.band ? ` (${(live.result.model.band.low * 100).toFixed(1)}–${(live.result.model.band.high * 100).toFixed(1)}%)` : ""}</div>
        </div>
        <div className="text-[10px] font-num opacity-75 mt-1" data-testid="poly-data-time">
          Data status: <b data-testid="poly-data-status">{live.stale ? "STALE" : (live.result?.freshness?.status || "LIVE")}</b> · {live.data?.quote_source || "book"} {utc(live.data?.book_ts)} · BRTI {utc(live.data?.index_ts)}
        </div>
        <Evidence e={live.evidence} />
        <p className="text-[10px] opacity-70 mt-2">Not a recommendation. No outcome is guaranteed.</p>
      </div>
    </section>
  );
};
